import React, { useCallback, useMemo } from 'react';
import type { FieldArrayRenderProps, FormikValues } from 'formik';
import { useFormikContext } from 'formik';
import { get } from 'lodash';

import { FsIndexPanel } from '~/components/core';
import type { IndexPanelSection } from '~/components/core/FsWrappers/FsIndexPanel/FsIndexPanel';

import { ADDITIONAL_INFORMATION_SECTIONS_PATH } from './Sections/AdditionalInformationSections/constants';
import { getLobSpecificAdditionalInfoSections } from './Sections/AdditionalInformationSections/LobSpecificSections';
import { reorderArrayField } from './reorderArrayField';

interface FnolConfigurationSectionsPanelProps {
  fnolConfiguration?: { lob?: string };
  isAddSectionEnabled: boolean;
  sectionsBefore: IndexPanelSection[];
  sectionsAfter: IndexPanelSection[];
  panelTitle: string;
  SectionWrapperComponent: React.FC;
  sectionContainerClassName?: string;
  handleScrollTopOffset: number;
}

// Extracted so the section list — and therefore the left-nav — reads LIVE Formik values: a custom
// section created in the dialog appears in the nav immediately, before the config is saved.
const FnolConfigurationSectionsPanel = ({
  fnolConfiguration,
  isAddSectionEnabled,
  sectionsBefore,
  sectionsAfter,
  panelTitle,
  SectionWrapperComponent,
  sectionContainerClassName,
  handleScrollTopOffset,
}: FnolConfigurationSectionsPanelProps) => {
  const { values, setFormikState } = useFormikContext<FormikValues>();

  // Reuses the configured-fields reorder primitive rather than `setFieldValue`: it moves the matching
  // `errors`/`touched` entries with the row and skips the synchronous full-form validation that makes a
  // drop lag on this form. It reads only `name` and `form.setFormikState`, not a real FieldArray.
  //
  // Depends on `setFormikState`, NOT on the context object: formik 2.2.6 builds a new context value on
  // every render, so closing over it would give this callback a fresh identity on every keystroke and
  // defeat the memo below — the one that exists precisely to stop rebuilding every child while typing.
  // `setFormikState` is declared `useCallback(..., [])` inside formik, so its identity is stable.
  const onReorderSections = useCallback(
    ({ fromIndex, toIndex }: { fromIndex: number; toIndex: number }) =>
      reorderArrayField(
        { name: ADDITIONAL_INFORMATION_SECTIONS_PATH, form: { setFormikState } } as unknown as FieldArrayRenderProps,
        fromIndex,
        toIndex
      ),
    [setFormikState]
  );
  // `?? []` not the lodash default: the field is nullable, and `get` substitutes its default only for
  // `undefined`, so a stored JSON `null` would reach `.map` below and blank the whole config screen.
  const rawSections: { id: string; desc: string }[] = get(values, ADDITIONAL_INFORMATION_SECTIONS_PATH) ?? [];

  // This component subscribes to Formik `values`, so it re-renders on every keystroke in the (large)
  // config form. Memoize the WHOLE section list — and therefore every child node it builds
  // (Supplementary Information, the custom cards, the Add Section panel) — so they are rebuilt only when a
  // section is actually added/renamed, not on every keystroke. The memo holds across keystrokes because
  // its inputs are stable: `sectionsBefore`/`sectionsAfter` come from FnolConfigurationScreen, which does
  // not re-render on form input (it subscribes to no Formik value), and `signature` is the minimal id+desc
  // slice of the sections. (A context/modal change up-tree can still re-render the parent and hand down new
  // array identities — that busts the memo for one render, which is fine; the point is the per-keystroke path.)
  const signature = rawSections.map((section) => `${section.id}:${section.desc}`).join('|');
  const sections: IndexPanelSection[] = useMemo(
    () => [
      ...sectionsBefore,
      ...(getLobSpecificAdditionalInfoSections(fnolConfiguration?.lob ?? '', {
        customSections: rawSections.map((section) => ({ id: section.id, desc: section.desc })),
        isAddSectionEnabled,
        onReorderSections,
      }) as IndexPanelSection[]),
      ...sectionsAfter,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature, sectionsBefore, sectionsAfter, fnolConfiguration?.lob, isAddSectionEnabled, onReorderSections]
  );

  return (
    <FsIndexPanel
      sections={sections}
      panelTitle={panelTitle}
      SectionWrapperComponent={SectionWrapperComponent}
      sectionContainerClassName={sectionContainerClassName}
      handleScrollTopOffset={handleScrollTopOffset}
    />
  );
};

export default FnolConfigurationSectionsPanel;
